// Single source of truth for the report sales pages' URLs and route titles.
// Deliberately dependency-free (same reasoning as
// `sales-forecast.routes-data.ts`): imported by the root router, the server
// route table, the report catalogue, and scripts/sitemap-reports.ts via tsx -
// none of which should pull the page copy or the order flow in with it.
//
// `reportId` is the backend's `ReportType` string, the same value as the
// `ReportTypes` enum key; spelled as a literal here so this file imports
// nothing.
//
// `label` is the short name for links and breadcrumbs (the footer, the page's
// own trail); the full product name stays `ReportData.title`.
//
// `parent` marks an industry page of a report that already has a general
// page: same product and checkout, its own copy and search terms. The general
// page must come first, because `reportPageUrl` returns the first page of a
// report id.

export const REPORT_PAGES_BASE_PATH = 'reports';

export const REPORT_PAGES = [
  {
    slug: 'community-compass',
    label: 'Neighborhood report',
    reportId: 'COMMUNITY_COMPASS',
    title: 'Neighborhood Report & Demographics for Any US Address | Map AI',
  },
  {
    slug: 'open-house-flyer',
    label: 'Open house flyer',
    reportId: 'OPEN_HOUSE_FLYER',
    title: 'Open House Flyer with Neighborhood Stats | Map AI',
  },
  {
    slug: 'business-site-traffic',
    label: 'Business Site Traffic',
    reportId: 'LOCATION_INTELLIGENCE',
    title: 'Traffic Count Report for a Retail Address | Map AI',
  },
  {
    slug: 'feasibility-study',
    label: 'Feasibility study',
    reportId: 'FEASIBILITY_STUDY',
    title: 'Bankable Feasibility Study for One Address | Map AI',
  },
  {
    slug: 'gas-station-feasibility-study',
    label: 'Gas station feasibility study',
    reportId: 'FEASIBILITY_STUDY',
    title: 'Gas Station Feasibility Study | Map AI',
    parent: 'feasibility-study',
  },
  {
    slug: 'convenience-store-feasibility-study',
    label: 'Convenience store feasibility study',
    reportId: 'FEASIBILITY_STUDY',
    title: 'Convenience Store Feasibility Study for Lenders | Map AI',
    parent: 'feasibility-study',
  },
  {
    slug: 'comparative-location-analysis',
    label: 'Comparative location analysis',
    reportId: 'COMPARATIVE_LOCATION',
    title: 'Comparative Location Analysis Report | Map AI',
  },
] as const;

export type ReportPageRoute = (typeof REPORT_PAGES)[number];
export type ReportPageSlug = ReportPageRoute['slug'];

/**
 * Where admins order the analysts' internal feasibility study: the same order
 * form as a sales page, with no sales copy. Unlisted - admin-guarded, not
 * prerendered, not in the sitemap.
 */
export const INTERNAL_FEASIBILITY_ORDER_PATH = `${REPORT_PAGES_BASE_PATH}/feasibility-study-internal`;

/** Fragment of a sales page's order form, which every order CTA lands on. */
export const REPORT_ORDER_FRAGMENT = 'order';

/** Route data key holding the slug for `ReportPageComponent`. */
export const REPORT_PAGE_SLUG_KEY = 'reportPageSlug';

export function reportPagePath(slug: ReportPageSlug): string {
  return `${REPORT_PAGES_BASE_PATH}/${slug}`;
}

/**
 * A report's industry pages (those with a `parent`), with the short name a
 * chip shows: "Gas station" for "Gas station feasibility study".
 */
export function industryReportPages(
  reportId: string
): { label: string; url: string }[] {
  return REPORT_PAGES.filter(
    (p) => p.reportId === reportId && 'parent' in p
  ).map((p) => ({
    label: p.label.replace(/ feasibility study$/i, ''),
    url: `/${reportPagePath(p.slug)}`,
  }));
}

/** The general page an industry page sits under, for its breadcrumbs. */
export function parentReportPage(
  page: ReportPageRoute
): ReportPageRoute | undefined {
  const parent = 'parent' in page ? page.parent : undefined;
  return REPORT_PAGES.find((p) => p.slug === parent);
}

/**
 * The sales page for a report id, or undefined for a report that has none
 * (the internal feasibility study). Absolute, for `routerLink` and hrefs.
 */
export function reportPageUrl(reportId: string): string | undefined {
  const page = REPORT_PAGES.find((p) => p.reportId === reportId);
  return page ? `/${reportPagePath(page.slug)}` : undefined;
}
